import { useEffect, useRef } from 'react';

export interface Variable {
  param_name: string;
  example: string;
}

interface UseExtractVariablesProps {
  text: string;
  parameterFormat: 'POSITIONAL' | 'NAMED';
  setError: (error: boolean, message?: string) => void;
  setVariables: (vars: Variable[]) => void;
  maxVariables?: number;
}

export const useExtractVariables = ({
  text,
  parameterFormat,
  setError,
  setVariables,
  maxVariables
}: UseExtractVariablesProps) => {
  const prevVarsRef = useRef<string>('');
  const prevErrorRef = useRef<string | null>(null);

  useEffect(() => {
    // Primero, detectamos TODOS los patrones que parecen variables
    const allVariablePatterns = /{{\s*([^{}]*?)\s*}}/g;
    const allMatches = text.match(allVariablePatterns) || [];

    // Extraemos el contenido de cada variable
    const allVariableContents = allMatches.map((match) => {
      const content = match.replace(/{{\s*|\s*}}/g, '');
      return { original: match, content: content.trim() };
    });

    // Verificamos si hay mezcla de formatos
    const hasNumbers = allVariableContents.some((v) => /^\d+$/.test(v.content));
    const hasNames = allVariableContents.some((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v.content));
    const hasEmpty = allVariableContents.some((v) => v.content === '');
    const hasInvalid = allVariableContents.some(
      (v) =>
        v.content !== '' && !/^\d+$/.test(v.content) && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v.content)
    );

    let validationErrorMessage = '';
    let isValid = true;

    // Validaciones según el formato
    if (parameterFormat === 'POSITIONAL') {
      if (hasNames) {
        validationErrorMessage =
          'En modo POSITIONAL no se permiten variables con letras. Usa {{1}}, {{2}}, etc.';
        isValid = false;
      } else if (hasInvalid && !hasEmpty) {
        validationErrorMessage = 'Las variables deben contener solo números en modo POSITIONAL';
        isValid = false;
      }

      // Verificar secuencia numérica correcta
      const numberVars = allVariableContents
        .filter((v) => /^\d+$/.test(v.content))
        .map((v) => parseInt(v.content))
        .sort((a, b) => a - b);

      if (numberVars.length > 0) {
        // Verificar que empiecen en 1 y sean consecutivos
        for (let i = 0; i < numberVars.length; i++) {
          if (numberVars[i] !== i + 1) {
            validationErrorMessage =
              'Las variables numéricas deben ser consecutivas empezando desde {{1}}';
            isValid = false;
            break;
          }
        }
      }
    } else if (parameterFormat === 'NAMED') {
      if (hasNumbers) {
        validationErrorMessage =
          'En modo NAMED no se permiten variables numéricas. Usa {{nombre}} en su lugar.';
        isValid = false;
      } else if (hasInvalid && !hasEmpty) {
        validationErrorMessage =
          'Las variables deben empezar con letra y contener solo letras, números y guiones bajos';
        isValid = false;
      }
    }

    // Validación de variables vacías
    if (hasEmpty) {
      validationErrorMessage = validationErrorMessage || 'No se permiten variables vacías {{}}';
      isValid = false;
    }

    // Filtrar solo las variables válidas según el formato
    let validTokens: string[] = [];
    if (parameterFormat === 'POSITIONAL') {
      validTokens = allVariableContents
        .filter((v) => /^\d+$/.test(v.content))
        .map((v) => v.content);
    } else {
      validTokens = allVariableContents
        .filter((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v.content))
        .map((v) => v.content);
    }

    // Eliminar duplicados manteniendo el orden
    validTokens = validTokens.filter((token, index, self) => self.indexOf(token) === index);

    // Validación de máximo de variables
    if (maxVariables !== undefined && validTokens.length > maxVariables) {
      validationErrorMessage = `Solo se permite ${maxVariables} variable${maxVariables > 1 ? 's' : ''} en el header.`;
      isValid = false;
    }

    const newError = !isValid;
    const newVariables = validTokens.map((token) => ({ param_name: token, example: '' }));
    const newVarsString = JSON.stringify(newVariables);

    if (prevErrorRef.current !== validationErrorMessage) {
      setError(newError, validationErrorMessage);
      prevErrorRef.current = validationErrorMessage;
    }

    if (prevVarsRef.current !== newVarsString) {
      setVariables(newVariables);
      prevVarsRef.current = newVarsString;
    }
  }, [text, parameterFormat, setError, setVariables, maxVariables]);
};
